/**
 * Корневой компонент.
 *
 * Полноценный роутер здесь избыточен: у мини-аппа четыре экрана и нет
 * ссылок извне на внутренние состояния. Переключение вкладок состоянием
 * экономит вес бандла и не ломает кнопку «назад» в Telegram.
 */

import { useEffect, useState } from "react";

import { Layout } from "./components/Layout";
import type { Screen, Tab } from "./navigation";
import { AdminPage } from "./pages/AdminPage";
import { CasesPage } from "./pages/CasesPage";
import { CrashPage } from "./pages/CrashPage";
import { HomePage } from "./pages/HomePage";
import { InventoryPage } from "./pages/InventoryPage";
import { PlinkoPage } from "./pages/PlinkoPage";
import { ProfilePage } from "./pages/ProfilePage";
import { SlotsPage } from "./pages/SlotsPage";
import { WalletPage } from "./pages/WalletPage";
import { WithdrawPage } from "./pages/WithdrawPage";
import { useBalanceSync } from "./hooks/useBalanceSync";
import { haptic, initTelegram, openChannel, useViewportHeight } from "./lib/telegram";
import { useSession } from "./store/session";

export function App() {
  const [screen, setScreen] = useState<Screen>("home");
  const { status, error, login, outsideTelegram, channel, channelUrl } = useSession();
  // Кошко краш, кейсы, слоты и плинко открываются с витрины игр, поэтому в нижней
  // навигации им соответствует вкладка «Игры»
  const tab: Tab =
    screen === "crash" || screen === "cases" || screen === "slots" || screen === "plinko"
      ? "home"
      : screen === "admin"
        ? "profile"
        : screen === "withdraw"
          ? "wallet"
          : screen;

  useEffect(() => {
    initTelegram();
    useViewportHeight();
    void login();
  }, [login]);

  useBalanceSync(screen);

  if (status === "loading" || status === "idle") {
    return <Splash />;
  }

  if (status === "needs_channel") {
    return (
      <ChannelGate
        channel={channel ?? "@koshkodrop"}
        channelUrl={channelUrl ?? "https://t.me/koshkodrop"}
        onRecheck={() => void login(true)}
      />
    );
  }

  if (status === "error") {
    return <LoginError message={error} outsideTelegram={outsideTelegram} onRetry={login} />;
  }

  return (
    <Layout tab={tab} onTabChange={setScreen}>
      {screen === "home" && <HomePage onOpen={setScreen} />}
      {screen === "crash" && <CrashPage onBack={() => setScreen("home")} />}
      {screen === "inventory" && <InventoryPage />}
      {screen === "cases" && <CasesPage onBack={() => setScreen("home")} />}
      {screen === "slots" && <SlotsPage onBack={() => setScreen("home")} />}
      {screen === "plinko" && <PlinkoPage onBack={() => setScreen("home")} />}
      {screen === "wallet" && <WalletPage onOpenWithdraw={() => setScreen("withdraw")} />}
      {screen === "withdraw" && <WithdrawPage onBack={() => setScreen("wallet")} />}
      {screen === "profile" && <ProfilePage onOpenAdmin={() => setScreen("admin")} />}
      {screen === "admin" && <AdminPage onBack={() => setScreen("profile")} />}
    </Layout>
  );
}

function Splash() {
  return (
    <div className="flex min-h-full flex-col items-center justify-center gap-4 bg-night-950">
      <span className="animate-pop text-6xl">🐈‍⬛</span>
      <p className="text-sm font-semibold text-white/45">Загружаю Кошкодроп…</p>
    </div>
  );
}

function ChannelGate({
  channel,
  channelUrl,
  onRecheck,
}: {
  channel: string;
  channelUrl: string;
  onRecheck: () => void;
}) {
  return (
    <div className="flex min-h-full flex-col items-center justify-center gap-5 bg-night-950 px-8 text-center">
      <span className="text-6xl">📢</span>
      <div className="space-y-2">
        <p className="text-lg font-extrabold text-white">Нужна подписка на канал</p>
        <p className="text-sm leading-relaxed text-white/45">
          Чтобы играть в Кошкодроп, подпишись на {channel} и нажми «Проверить».
        </p>
      </div>
      <button
        type="button"
        onClick={() => {
          haptic.tap("medium");
          openChannel(channelUrl);
        }}
        className="w-full max-w-xs rounded-2xl bg-paws-500 py-4 text-base font-extrabold text-night-950"
      >
        Подписаться
      </button>
      <button
        type="button"
        onClick={() => {
          haptic.select();
          onRecheck();
        }}
        className="w-full max-w-xs rounded-2xl border border-white/10 py-3 text-sm font-bold text-white/60"
      >
        Проверить подписку
      </button>
    </div>
  );
}

function LoginError({
  message,
  outsideTelegram,
  onRetry,
}: {
  message: string | null;
  outsideTelegram: boolean;
  onRetry: () => void;
}) {
  return (
    <div className="flex min-h-full flex-col items-center justify-center gap-4 bg-night-950 px-8 text-center">
      <span className="text-6xl">🙀</span>
      <p className="text-base font-bold text-white">{message ?? "Не удалось войти"}</p>

      {outsideTelegram ? (
        <p className="text-xs leading-relaxed text-white/40">
          Кошкодроп работает внутри Telegram: вход подтверждается подписью
          мессенджера, и без неё сервер не может определить игрока.
        </p>
      ) : (
        <button
          type="button"
          onClick={onRetry}
          className="rounded-2xl bg-paws-500 px-6 py-3 text-sm font-extrabold text-night-950"
        >
          Попробовать снова
        </button>
      )}
    </div>
  );
}
